/**
 * useScenarios hook - Access scenarios list with reactivity
 */

import { useState, useEffect } from 'react'
import { memoryStateService } from '../../core/state/memory-store'
import { projectScenariosForUI } from '../../core/queries/projections'
import { userContextService } from '../../core/infrastructure/user-context-service'
import { serverSyncService } from '../../core/infrastructure/server-sync-service'
import type { Scenario } from '../../core/domain/models'
import { logger } from '../../core/utils/logger'

export function useScenarios() {
  const [scenarios, setScenarios] = useState<Scenario[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [currentUser, setCurrentUser] = useState(() => userContextService.getCurrentUser())
  const [syncing, setSyncing] = useState(() => serverSyncService.isUserGroupSyncInProgress())

  // Subscribe to auth state changes
  useEffect(() => {
    const subscription = userContextService.onAuthStateChanged((user) => {
      console.log('[useScenarios] Auth state changed, new user:', user)
      setCurrentUser(user)
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [])

  // Subscribe to user group sync state (server-side scenario refresh)
  useEffect(() => {
    const unsubscribe = serverSyncService.onUserGroupSyncChange(setSyncing)
    return unsubscribe
  }, [])

  useEffect(() => {
    let mounted = true
    let unsubscribe: (() => void) | null = null

    console.log('[useScenarios] Effect triggered, currentUser:', currentUser)

    // Initial load
    const loadScenarios = async () => {
      // Wait for auth to complete before accessing IndexedDB
      if (!currentUser) {
        if (mounted) {
          logger.debug('No current user, skipping scenario load')
          setScenarios([])
          setLoading(false)
        }
        return
      }

      try {
        // Note: Don't setLoading(true) here. The initial useState(true) covers the
        // first load. Re-setting it on subsequent runs (e.g. when currentUser changes
        // from null to authenticated) causes a brief flash of the "Loading scenarios..."
        // spinner between the "Syncing your scenarios..." states, because the effect
        // fires after React renders the syncing indicator.

        // Load all scenarios from IndexedDB into memory state
        await memoryStateService.loadAllScenarios()
        // Apply only scenario-level pending commands (Create/Rename/Delete Scenario)
        // Skips list/item commands since homepage only shows name/description
        await memoryStateService.applyPendingScenarioCommands()
        // Get scenarios from memory state (includes optimistic updates)
        const data = memoryStateService.getAllScenarios()
        if (mounted) {
          setScenarios(projectScenariosForUI(data))
          setError(null)
        }
      } catch (err: any) {
        console.error('[useScenarios] Error loading scenarios:', err)
        if (mounted) {
          setError(err.message || 'Failed to load scenarios')
        }
      } finally {
        if (mounted) {
          setLoading(false)
        }
      }
    }

    loadScenarios()

    // Subscribe to changes from memory state (any scenario changes)
    // Pass needsFullData=false since we only need metadata for scenarios list
    unsubscribe = memoryStateService.onStateChange(null, () => {
      if (mounted) {
        // Re-read scenarios from memory state (already includes pending commands)
        const data = memoryStateService.getAllScenarios()
        setScenarios(projectScenariosForUI(data))
      }
    }, false) // false = metadata only, don't need lists/items

    return () => {
      mounted = false
      if (unsubscribe) unsubscribe()
    }
  }, [currentUser])

  return { scenarios, loading, error, syncing }
}
